import { ColDef } from 'ag-grid-community';
import { loginHistoryByUserId } from '../../api/accountsApi';
import { formatDate, formatDateTime } from '../../utils/date';

/**
 * Helper function to calculate session duration
 * @param loginTime - The login time
 * @param logoutTime - The logout time (optional)
 * @param refreshTokenEvents - Array of refresh token events (optional)
 * @returns { hours: number, minutes: number, endTime: Date | null }
 */
const calculateSessionDuration = (
  loginTime: string,
  logoutTime?: string,
  refreshTokenEvents?: { time: string }[]
): { hours: number; minutes: number; endTime: Date | null } => {
  const loginDate = new Date(loginTime);

  // If logout time is available
  if (logoutTime) {
    const logoutDate = new Date(logoutTime);
    const sessionDuration = Math.abs(logoutDate.getTime() - loginDate.getTime());
    const hours = Math.floor(sessionDuration / (1000 * 60 * 60));
    const minutes = Math.floor((sessionDuration % (1000 * 60 * 60)) / (1000 * 60));
    return { hours, minutes, endTime: logoutDate };
  }

  // If refresh tokens are available, find the latest refresh token time
  let latestRefreshTokenTime: Date | null = null;
  if (refreshTokenEvents && refreshTokenEvents.length > 0) {
    latestRefreshTokenTime = new Date(
      Math.max(...refreshTokenEvents.map(event => new Date(event.time).getTime()))
    );
  }

  // Determine the auto logout time
  const autoLogoutTime = latestRefreshTokenTime
    ? new Date(latestRefreshTokenTime.getTime() + 3 * 60 * 60 * 1000) // Add 3 hours to the latest refresh token time
    : new Date(loginDate.getTime() + 3 * 60 * 60 * 1000); // Add 3 hours to login time if no refresh token

  const now = new Date();
  const endTime = now > autoLogoutTime ? autoLogoutTime : null;
  const sessionDuration = Math.abs((endTime || now).getTime() - loginDate.getTime());
  const hours = Math.floor(sessionDuration / (1000 * 60 * 60));
  const minutes = Math.floor((sessionDuration % (1000 * 60 * 60)) / (1000 * 60));

  return { hours, minutes, endTime };
};

/**
 * Function to calculate session time and determine logout status
 * @param loginTime - The login time
 * @param logoutTime - The logout time (optional)
 * @param refreshTokenEvents - Array of refresh token events (optional)
 * @returns Session status string
 */
const calculateSessionTime = (
  loginTime: string,
  logoutTime?: string,
  refreshTokenEvents?: { time: string }[]
): string => {
  const { hours, minutes, endTime } = calculateSessionDuration(loginTime, logoutTime, refreshTokenEvents);

  if (logoutTime) {
    return `Logout after ${hours}H:${minutes}M at ${formatDateTime(logoutTime)}`;
  }

  if (endTime) {
    return `Auto Logout after ${hours}H:${minutes}M at ${formatDateTime(endTime.toISOString())}`;
  }

  return `Online since ${hours}H:${minutes}M`;
};

/**
 * Function to parse userAgent string
 * @param userAgent - The userAgent string
 * @returns Parsed browser and system information
 */
const parseUserAgent = (userAgent: string): string => {
  const browserMatch = userAgent.match(/(Chrome|Firefox|Safari|Edge|Opera|MSIE|Trident)[\/\s]?([\d.]+)/i);
  const systemMatch = userAgent.match(/\(([^)]+)\)/);
  const browser = browserMatch ? browserMatch[1] : 'Unknown Browser';
  const system = systemMatch ? systemMatch[1].split(';')[0] : 'Unknown System';
  return `${browser}, ${system}`;
};

/**
 * Function to format token refresh times
 * @param refreshTokenEvents - Array of refresh token events
 * @returns Formatted string of refresh times grouped by date
 */
const formatRefreshTokenTimes = (refreshTokenEvents: { time: string }[]): string => {
  if (!refreshTokenEvents || refreshTokenEvents.length === 0) {
    return '-';
  }

  const groupedByDate: Record<string, string[]> = {};

  refreshTokenEvents.forEach(event => {
    const eventDate = new Date(event.time);
    const dateKey = formatDate(eventDate.toISOString()); // Convert Date to ISO string for formatDate
    const timeValue = eventDate.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' }); // Format as HH:mm

    if (!groupedByDate[dateKey]) {
      groupedByDate[dateKey] = [];
    }
    groupedByDate[dateKey].push(timeValue);
  });

  return Object.entries(groupedByDate)
    .map(([date, times]) => `${date} [${times.join(', ')}]`)
    .join(', ');
};

export const loginHistoryTableColumns: ColDef[] = [
  { headerName: 'Login Time', field: 'loginTime', valueFormatter: (params) => formatDateTime(params.value) }, 
  {
    headerName: 'Session Status',
    field: 'sessionTime',
    valueGetter: (params) => calculateSessionTime(params.data.loginTime, params.data.logoutTime, params.data.refreshTokenEvents), // Use the calculation function
  },
  {
    headerName: 'Token Refresh Times',
    field: 'refreshTokenEvents',
    valueFormatter: (params) => formatRefreshTokenTimes(params.value), // Format refresh token times
  },
  { headerName: 'IP Address', field: 'ipAddress' },
  {
    headerName: 'User Agent',
    field: 'userAgent',
    valueFormatter: (params) => parseUserAgent(params.value), // Use the parsing function
  },
];

export const loginHistoryConfig = {
  entityName: 'Login History',
  redirectPath: '/login-history',
  loginHistoryTableColumns, // Include table columns
  api: {
    list: loginHistoryByUserId,
  },
};

/**
 * Function to calculate total session time for all records
 * @param records - Array of login history records
 * @returns Total session time as a string
 */
// Define the LoginHistoryRecord type
type LoginHistoryRecord = {
  loginTime: string;
  logoutTime?: string;
  refreshTokenEvents?: { time: string }[];
};

const calculateTotalSessionTime = (records: LoginHistoryRecord[]): string => {
  let totalMinutes = 0;

  records.forEach(record => {
    const { hours, minutes } = calculateSessionDuration(record.loginTime, record.logoutTime, record.refreshTokenEvents);
    totalMinutes += hours * 60 + minutes;
  });

  const totalHours = Math.floor(totalMinutes / 60);
  const remainingMinutes = totalMinutes % 60;

  return `${totalHours}H:${remainingMinutes}M`;
};

export { calculateSessionTime, calculateTotalSessionTime };